/**
 * KonvaCommonUtils — lightweight utilities shared across KonvaElements, KonvaUtils,
 * and KonvaQuestionType components.
 * Extracted into a separate file to break the circular dependency:
 * KonvaUtils → KonvaElements → KonvaUtils
 */
import _ from "lodash";
import { KONVA_EVENTS } from "./Konva/KonvaConstants";

let canvasSize = { height: 0, width: 0 };

export const setCanvasSize = size => {
  canvasSize = size;
};

/**
 *
 * @param {*} item
 * @returns generated element unique name
 */
export const generateElementName = (item = {}) => {
  const id = _.get(item, "id");
  const type = _.get(item, "type");
  const category = _.get(item, "category");
  return _.join([category, type, id], "#");
};

/**
 *
 * @param {*} key
 * @param {*} cb
 * @returns Document event listener and cleanup function
 */
export const listenDocumentEvent = (key, cb = () => {}) => {
  document.addEventListener(key, cb);
  return () => {
    document.removeEventListener(key, cb);
  };
};

/**
 *
 * @param {*} text
 * @returns Checking text direction
 */
export const isTextRTL = text => {
  return /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/.test(text);
};

/**
 *
 * @param {*} standardSize
 * @return size relative to canvas considering 778 as standard size
 */
export const getCanvasRelativeSize = (
  standardSize = [],
  returnCanvasSize = false,
  useLowerEnd = true
) => {
  const height = _.get(canvasSize, "height", 0);
  const width = _.get(canvasSize, "width", 0);
  if (!height || !width) return standardSize;

  if (returnCanvasSize && !standardSize) return { height, width };

  if (_.isNumber(standardSize)) {
    return width < 800 && useLowerEnd
      ? standardSize
      : standardSize * ((width || 778) / 778);
  }

  return _.map(standardSize, _size => {
    return width < 800 && useLowerEnd ? _size : _size * ((width || 778) / 778);
  });
};

/**
 *
 * @param {*} stage
 * @param {*} callbacks
 * @description konva callback to regular function converter
 */
export const listenBackEvents = (stage, transformer, callbacks) => {
  if (stage) stage.off();
  if (transformer) transformer.off();

  const cb = func => _.get(callbacks, func);

  const registerOn = ([target, eventType, callback]) => {
    if (target && _.isFunction(_.get(callbacks, callback))) {
      target.off(eventType);
      target.on(eventType, cb(callback));
    }
  };

  const ALL_KONVA_EVENTS = [
    [stage, KONVA_EVENTS.DROP, "onDrop"],
    [stage, KONVA_EVENTS.CLICK, "onClick"],
    [stage, KONVA_EVENTS.DRAGEND, "onDragEnd"],
    [stage, KONVA_EVENTS.MOUSEUP, "onMouseUp"],
    [stage, KONVA_EVENTS.DRAGMOVE, "onDragMove"],
    [stage, KONVA_EVENTS.DRAGOVER, "onDragOver"],
    [stage, KONVA_EVENTS.MOUSEMOVE, "onMouseMove"],
    [stage, KONVA_EVENTS.MOUSEDOWN, "onMouseDown"],
    [stage, KONVA_EVENTS.MOUSEOVER, "onMouseOver"],
    [stage, KONVA_EVENTS.DRAGSTART, "onDragStart"],
    [stage, KONVA_EVENTS.DRAGENTER, "onDragEnter"],
    [stage, KONVA_EVENTS.DRAGLEAVE, "onDragLeave"],
    [transformer, KONVA_EVENTS.TRANSFORM, "onTransform"],
    [transformer, KONVA_EVENTS.TRANSFORMEND, "onTransformEnd"],
    [transformer, KONVA_EVENTS.TRANSFORMERATTACHED, "onTransformerAttached"],
    [transformer, KONVA_EVENTS.TRANSFORMERDETACHED, "onTransformerDetached"],
  ];

  _.forEach(ALL_KONVA_EVENTS, event => registerOn(event));
};
